import React from "react";
import { Shield, CheckCircle2, MapPin, Sparkles, Users, Building2 } from "lucide-react";
import { Link } from "react-router-dom";
import { BRAND } from "../constants/brand";

export default function About() {
  return (
    <>
      <section className="relative overflow-hidden border-b border-neutral-200 bg-neutral-950 text-white grain" data-testid="about-hero">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
          <span className="text-xs font-semibold uppercase tracking-widest text-lime-400">About us</span>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-black leading-tight tracking-tight md:text-6xl" data-testid="about-title">
            We built EarlyPrint because print orders shouldn't feel like a <span className="text-lime-400">gamble</span>.
          </h1>
          <p className="mt-6 max-w-2xl text-neutral-300">
            India's printing industry moves ₹80,000 crore a year — yet almost none of it happens online. EarlyPrint is the layer that was always missing between print shops and the people who need what they make.
          </p>
        </div>
      </section>

      <section className="border-b border-neutral-200" data-testid="about-story">
        <div className="mx-auto grid max-w-7xl gap-12 px-4 py-20 md:grid-cols-12 md:px-6 md:py-28">
          <div className="md:col-span-5">
            <span className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Our story</span>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">From offline chaos to one clean screen.</h2>
          </div>
          <div className="space-y-4 text-neutral-700 md:col-span-7">
            <p>Every day, business owners drive across town to place a print order, hand over a pen drive, get a verbal price, and hope the shop calls back when it's ready. There is no comparison, no tracking, and no protection if something goes wrong.</p>
            <p>EarlyPrint fixes exactly that gap — for the customer who has been quoted three different prices for the same job, and for the print shop owner whose machine sits idle between walk-ins even though their work is excellent.</p>
            <p>We are not replacing print shops. We are building the layer that was always missing between them and the people who need what they make — the same way ride-hailing and food-delivery apps built a layer over drivers and restaurants that already existed.</p>
          </div>
        </div>
      </section>

      <section className="border-b border-neutral-200 bg-neutral-50" data-testid="about-mission">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
          <span className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Our mission</span>
          <h2 className="mt-2 max-w-3xl font-display text-3xl font-bold tracking-tight md:text-5xl">
            Make ordering print as simple as ordering food — <span className="text-lime-500">for everyone in India</span>.
          </h2>
        </div>
      </section>

      <section className="border-b border-neutral-200" data-testid="about-safety">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
          <span className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Safety by design</span>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">How we keep every order safe.</h2>
          <div className="mt-10 grid gap-4 md:grid-cols-2">
            {[
              { icon: Shield, t: "Escrow-protected payments", d: "Your payment is held securely and released to the vendor only after you confirm delivery." },
              { icon: CheckCircle2, t: "Verified vendors only", d: "Every print shop is personally vetted before it can accept a single order." },
              { icon: Sparkles, t: "Live, transparent bidding", d: "Vendors compete openly, so the price you see is a real quote — not a guess over the phone." },
              { icon: MapPin, t: "Real-time tracking", d: "Every order shows its exact stage, from file received to out for delivery." },
            ].map((p) => (
              <div key={p.t} className="rounded-lg border border-neutral-200 bg-white p-6" data-testid={`safety-${p.t.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className="flex h-10 w-10 items-center justify-center rounded-md bg-lime-400 text-neutral-950"><p.icon size={18} /></div>
                <h3 className="mt-4 font-display text-lg font-bold">{p.t}</h3>
                <p className="mt-2 text-sm text-neutral-600">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-neutral-200 bg-neutral-50" data-testid="about-audience">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
          <span className="text-xs font-semibold uppercase tracking-widest text-neutral-500">Who we serve</span>
          <h2 className="mt-2 font-display text-3xl font-bold tracking-tight md:text-4xl">Made for everyone in the print supply chain.</h2>
          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              { icon: Users, t: "Everyday customers", d: "A print order placed from a phone in two minutes, at a fair, comparable price, delivered to their door." },
              { icon: Building2, t: "Businesses & startups", d: "Bulk stationery, packaging and marketing print with one dashboard, full history, and GST-ready invoices." },
              { icon: Sparkles, t: "Print shop owners", d: "A steady stream of verified, pre-paid orders — no lead fees, no upfront cost, guaranteed on-time payment via escrow." },
            ].map((a) => (
              <div key={a.t} className="rounded-lg border border-neutral-200 bg-white p-6" data-testid={`audience-${a.t.replace(/\s+/g, "-").toLowerCase()}`}>
                <a.icon size={22} className="text-lime-500" />
                <h3 className="mt-3 font-display text-lg font-bold">{a.t}</h3>
                <p className="mt-2 text-sm text-neutral-600">{a.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-b border-neutral-200 bg-neutral-950 text-white grain">
        <div className="mx-auto max-w-7xl px-4 py-20 md:px-6 md:py-28">
          <span className="text-xs font-semibold uppercase tracking-widest text-lime-400">Team & company</span>
          <h2 className="mt-2 max-w-3xl font-display text-3xl font-bold tracking-tight md:text-4xl">EarlyPrint is built and operated in {BRAND.location}.</h2>
          <p className="mt-4 max-w-2xl text-neutral-300">Currently serving Delhi NCR with a nationwide rollout planned as our verified-vendor network grows.</p>
          <div className="mt-8">
            <Link to="/contact" className="btn-offset inline-block rounded-md bg-lime-400 px-5 py-3 text-sm font-bold text-neutral-950 hover:bg-lime-300" data-testid="about-cta-contact">Talk to us</Link>
          </div>
        </div>
      </section>
    </>
  );
}
